import { zodResolver } from '@hookform/resolvers/zod';
import dompurify from 'dompurify';
import { useForm, useFormContext } from 'react-hook-form';
import { z } from 'zod';
import { useGenerateLinkedInMessageOutreach, useLinkedInMessageOutreach } from '../../../../api/useOutreachCandidate';
import UIStrings from '@/Utils/UIStrings';
import ValidationErrors from '@/Utils/ValidationErrors';

export const LinkedInMessageLimit = 1900;

const linkedInMessageSchema = z.object({
    messageHtml: z.string().min(1),
    htmlTextLength: z
        .number()
        .positive(ValidationErrors.Required)
        .max(LinkedInMessageLimit, ValidationErrors.PleaseLimitDescriptionError),
    subject: z
        .string()
        .min(1, ValidationErrors.Required)
        .max(200, { error: ({ maximum }) => UIStrings.MaxSymbolsValidationError(maximum) }),
});

export type LinkedInMessageTemplateForm = z.infer<typeof linkedInMessageSchema>;

export const useLinkedInMessageForm = () => {
    // this should be the only place to handle ai generated outreach
    // unless if you are using useSWR/useSWRImmut
    const { trigger } = useGenerateLinkedInMessageOutreach();
    const { data: savedTemplate } = useLinkedInMessageOutreach();

    return useForm<LinkedInMessageTemplateForm>({
        resolver: zodResolver(linkedInMessageSchema),
        mode: 'all',
        defaultValues: async () => {
            const defaultValues: LinkedInMessageTemplateForm = {
                messageHtml: '',
                htmlTextLength: 0,
                subject: '',
            };

            if (savedTemplate) {
                const parser = new DOMParser();
                const dom = parser.parseFromString(savedTemplate.message, 'text/html');

                defaultValues.messageHtml = dompurify.sanitize(savedTemplate.message);
                defaultValues.htmlTextLength = dom.body?.textContent?.length || 0;
                defaultValues.subject = savedTemplate.subject;
            } else {
                try {
                    const generatedOutreach = await trigger();
                    const messageHtml = generatedMessageToHtml(generatedOutreach.message);

                    defaultValues.messageHtml = dompurify.sanitize(messageHtml);
                    defaultValues.htmlTextLength = generatedOutreach.message.length;
                    defaultValues.subject = generatedOutreach.subject;
                } catch {
                    return defaultValues;
                }
            }

            return defaultValues;
        },
    });
};

export const useLinkedInMessageFormContext = () => useFormContext<LinkedInMessageTemplateForm>();

// only use for outreach strings generated by AI
const generatedMessageToHtml = (messageBody: string) => {
    const nodes = messageBody.split(/\n/g);
    const encodedMessageBody = nodes
        // fully encode to html paragraphs
        .map((node) => `<p>${node}</p>`)
        // transform your nodes here
        .map((node, i, arr) => {
            const listSearchValue = '<p>- ';

            // transform node starting with dash to unordered list
            if (node.startsWith(listSearchValue)) {
                const newNode = `<li>${node.replace(listSearchValue, '<p>')}</li>`;

                if (!arr[i - 1].startsWith(listSearchValue)) {
                    return `<ul>${newNode}`;
                } else if (!arr[i + 1].startsWith(listSearchValue)) {
                    return `${newNode}</ul>`;
                } else {
                    return newNode;
                }
            } else {
                return node;
            }
        })
        .join('');

    return encodedMessageBody;
};
